/*  MUI  */
import { Box } from '@mui/material';

/* Shared */
import { ModalFormFooter } from 'application/Shared/Modals';
import { ProviderType } from 'application/Shared/types/tenantProvider';

/* Hooks */
import { useUpdateSession } from 'hooks/redux/Selectors/session';

/*  React  */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

/* Internal Components */
import { Modal } from '../../Modals/Components/Modal';
import { ModalBody } from '../../Modals/Components/ModalBody';
import {
    INTEGRATION_OPTION_GOOGLE_AUTOMATED,
    INTEGRATION_OPTION_MICROSOFT_ENTRA_ID_AUTOMATED
} from './Components/IntegrationSelector';
import { NoIntegrationForm } from './Forms/NoIntegrationForm';
import { createClient } from './Forms/functions';
import { IntegrationWizard } from './IntegrationWizard';
import { SetupIntegration } from './SetupIntegration';
import { UseIntegrationSelector } from './UseIntegrationSelector';

export const AddClient = ({
    filterExistingSyncedTenants = true,
    formFooterComponent,
    handleUseAnIntegrationMethodChange,
    onboarding,
    onSuccess,
    setupIntegrationProps,
    skipToChoosingIntegration,
    tenantToEditIntegration,
    useAnIntegrationMethod,
    useIntegrationWizard = true,
    skipAnimation
}) => {
    const { t } = useTranslation();
    const updateSession = useUpdateSession();

    const [integrationType, setIntegrationType] = useState(
        tenantToEditIntegration?.providerType === ProviderType.Google
            ? INTEGRATION_OPTION_GOOGLE_AUTOMATED
            : INTEGRATION_OPTION_MICROSOFT_ENTRA_ID_AUTOMATED
    );

    // If onboarding, then the selection of NoIntegrationForm has already been made, so set useIntegrationSubmitted to true
    const [useIntegrationSubmitted, setUseIntegrationSubmitted] = useState(
        onboarding ||
            (tenantToEditIntegration && skipToChoosingIntegration) ||
            false
    );

    const showInitial = !useIntegrationSubmitted || !useIntegrationWizard;
    const showWizard =
        useIntegrationSubmitted && useAnIntegrationMethod === 'true';
    const showIntegrationForm =
        !useIntegrationWizard && useAnIntegrationMethod === 'true';

    return (
        <Box width="100%" data-testid="addClient">
            {showInitial && (
                <UseIntegrationSelector
                    handleUseAnIntegrationMethodChange={
                        handleUseAnIntegrationMethodChange
                    }
                    onSuccess={onSuccess}
                    setUseIntegrationSubmitted={setUseIntegrationSubmitted}
                    useAnIntegrationMethod={useAnIntegrationMethod}
                    useIntegrationWizard={useIntegrationWizard}
                />
            )}
            {showWizard && (
                <IntegrationWizard
                    filterExistingSyncedTenants={filterExistingSyncedTenants}
                    integrationType={integrationType}
                    onSuccess={onSuccess}
                    setIntegrationType={setIntegrationType}
                    setUseIntegrationSubmitted={setUseIntegrationSubmitted}
                    tenantToEditIntegration={tenantToEditIntegration}
                    skipAnimation={skipAnimation}
                />
            )}
            {showIntegrationForm && (
                <SetupIntegration
                    filterExistingSyncedTenants={filterExistingSyncedTenants}
                    formFooterComponent={formFooterComponent}
                    integrationType={integrationType}
                    onSuccess={onSuccess}
                    setIntegrationType={setIntegrationType}
                    useIntegrationWizard={useIntegrationWizard}
                    {...setupIntegrationProps}
                />
            )}
            {((useIntegrationSubmitted &&
                !(useAnIntegrationMethod === 'true')) ||
                // If onboarding, go directly to the NoIntegrationForm
                onboarding) && (
                <NoIntegrationForm
                    onSubmit={(values) =>
                        createClient({
                            onSuccess,
                            t,
                            updateSession,
                            values
                        })
                    }
                >
                    {useIntegrationWizard ? (
                        <ModalFormFooter
                            onClose={() => {
                                setUseIntegrationSubmitted(onboarding || false);
                                if (onboarding) {
                                    onSuccess(); // Close the modal
                                }
                            }}
                            onCloseBtnText={
                                onboarding
                                    ? t('Common.close')
                                    : t('Common.back')
                            }
                        />
                    ) : (
                        formFooterComponent
                    )}
                </NoIntegrationForm>
            )}
        </Box>
    );
};

export const AddClientModal = ({
    filterExistingSyncedTenants = true,
    formFooterComponent,
    handleUseAnIntegrationMethodChange,
    onboarding,
    onSuccess,
    show,
    showDescription,
    skipToChoosingIntegration,
    tenantToEditIntegration,
    useAnIntegrationMethod,
    skipAnimation
}) => {
    const { t } = useTranslation();

    return (
        <Modal
            disableEasyClose
            onClose={onSuccess}
            open={show}
            paperProps={{
                minWidth: ['600px', '800px', '1000px', '1200px', '1300px'],
                width: '100%'
            }}
            title={
                tenantToEditIntegration
                    ? t('PartnerOrganization.editClient')
                    : t('PartnerOrganization.add_client')
            }
            titleProps={{ showClose: true }}
        >
            <ModalBody style={{ padding: 0 }}>
                <AddClient
                    filterExistingSyncedTenants={filterExistingSyncedTenants}
                    formFooterComponent={formFooterComponent}
                    handleUseAnIntegrationMethodChange={
                        handleUseAnIntegrationMethodChange
                    }
                    onboarding={onboarding}
                    onSuccess={onSuccess}
                    showDescription={showDescription}
                    skipToChoosingIntegration={skipToChoosingIntegration}
                    tenantToEditIntegration={tenantToEditIntegration}
                    useAnIntegrationMethod={useAnIntegrationMethod}
                    skipAnimation={skipAnimation}
                />
            </ModalBody>
        </Modal>
    );
};
